import { Link } from "react-router-dom";
import { ArrowUpRight, Shield, Sparkles, ClipboardCheck, Award } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CountUp } from "@/components/CountUp";
import { IMAGES, PROCESS } from "@/lib/site";

export default function Manufacturing() {
  return (
    <div data-testid="manufacturing-page">
      <PageHero
        overline="Manufacturing"
        title="10,000+ sq ft of precision engineering."
        subtitle="Inside our Morbi facility, precision fabrication meets craftsman finishing. Every Stenzia display is built, inspected and finished under one roof."
        image={IMAGES.manufacturing}
      />

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <Reveal className="lg:col-span-6 relative rounded-3xl overflow-hidden aspect-[4/5] shadow-2xl">
              <img src={IMAGES.manufacturingImage} alt="Manufacturing process" loading="lazy" className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-[1500ms]" />
              <div className="absolute inset-0 bg-gradient-to-tr from-charcoal/30 via-transparent to-transparent" />
            </Reveal>
            <Reveal delay={1} className="lg:col-span-6">
              <div className="overline text-stenzia-green"><span className="kbd-line" /> The Factory</div>
              <h2 className="mt-6 font-display font-light text-4xl md:text-5xl tracking-tighter text-charcoal leading-[1.05]">
                Engineering you can stand on.
              </h2>
              <p className="mt-6 text-charcoal/65 leading-relaxed">
                Our 10,000+ sq ft facility integrates the full manufacturing workflow — CAD design, laser cutting, certified welding, premium electrostatic powder coating, careful assembly and quality inspection at every stage. The result is a display system you can install with confidence in any showroom, anywhere in the world.
              </p>

              <div className="mt-8 grid grid-cols-2 gap-6">
                {[
                  { num: 10000, suffix: "+", label: "Sq. Ft. Manufacturing Facility" },
                  { num: 20, suffix: "+", label: "Years of Engineering & Manufacturing Experience" },
                  { num: 51, suffix: "+", label: "Successfully Installed Display Systems" },
                  { num: 20, suffix: "+", label: "Product Variants" },
                ].map((s) => (
                  <div key={s.label}>
                    <div className="font-display text-3xl text-charcoal tabular-nums">
                      <CountUp end={s.num} suffix={s.suffix} />
                    </div>
                    <div className="text-xs mt-1 text-charcoal/55 tracking-wider uppercase">{s.label}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-charcoal text-white relative overflow-hidden">
        <div className="grain absolute inset-0" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="overline text-stenzia-gold"><span className="kbd-line" /> Production Steps</div>
          <h2 className="mt-6 font-display font-light text-4xl md:text-5xl tracking-tighter leading-[1.05] max-w-3xl">
            Seven stages. Zero shortcuts.
          </h2>

          <div className="mt-16 space-y-px">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i}>
                <div data-testid={`mfg-step-${p.step}`} className="grid grid-cols-1 md:grid-cols-12 gap-6 py-8 border-t border-white/10 hover:bg-white/5 transition-colors px-2 md:px-6 group">
                  <div className="md:col-span-1 font-display text-stenzia-gold font-semibold text-lg group-hover:scale-110 group-hover:-translate-x-1 transition-all duration-500 origin-left">{p.step}</div>
                  <div className="md:col-span-3 font-display text-xl md:text-2xl tracking-tight">{p.title}</div>
                  <div className="md:col-span-8 text-white/65 leading-relaxed">{p.body}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* QUALITY + PROMISE GRID */}
      <section className="py-20 md:py-28 bg-[#F6F7F5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="overline text-stenzia-green"><span className="kbd-line" /> Engineering Standards</div>
            <h2 className="mt-6 font-display font-light text-4xl md:text-5xl tracking-tighter text-charcoal leading-[1.05] max-w-3xl">
              Built for the long term. Inspected at every stage.
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                icon: Shield,
                title: "Heavy Duty Construction",
                body: "Heavy duty CRC steel construction with reinforced welded joints — engineered for continuous showroom usage.",
              },
              {
                icon: Sparkles,
                title: "Premium Powder Coating",
                body: "Premium electrostatic powder coating designed for long-lasting indoor showroom use and superior surface protection.",
              },
              {
                icon: ClipboardCheck,
                title: "Quality Inspection",
                body: "Every STENZIA product undergoes careful inspection during fabrication, welding, assembly, finishing and final dispatch to ensure premium quality and reliable showroom performance.",
              },
              {
                icon: Award,
                title: "Built for Long-Term Performance",
                body: "Manufactured using premium raw materials and precision engineering to deliver reliable showroom performance for many years under normal operating conditions.",
              },
            ].map((c) => (
              <div key={c.title} className="bg-white rounded-2xl p-6 md:p-8 border border-charcoal/10 hover:border-stenzia-green/25 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-400">
                <div className="w-12 h-12 rounded-xl bg-stenzia-green/10 text-stenzia-green flex items-center justify-center">
                  <c.icon size={20} />
                </div>
                <div className="mt-5 font-display text-lg text-charcoal tracking-tight">{c.title}</div>
                <p className="mt-2 text-[13px] text-charcoal/65 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <div className="overline text-stenzia-green">Visit the factory</div>
          <h2 className="mt-6 font-display font-light text-4xl md:text-5xl tracking-tighter text-charcoal leading-[1.05] max-w-3xl mx-auto">
            Every unit. Every weld. Inspected, signed off, ready to install.
          </h2>
          <Link to="/contact" data-testid="mfg-cta-btn" className="mt-8 inline-flex btn-primary">Tour our facility <ArrowUpRight size={18} /></Link>
        </div>
      </section>
    </div>
  );
}
